'use client'

import { useState } from 'react'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/shared/api-client'
import { useSession } from '@marketplace-web/shared/session'

import {
  getFeedbackProblemOptions,
  sendFeedbackProblemFeedback,
  transformFeedbackProblemOptions,
} from '@marketplace-web/domain/conversation-feedback-data'
import {
  ConversationActionButton,
  ConversationActionProps,
} from '@marketplace-web/messaging/conversation-action-feature'
import useHelpCenterSessionTracking from 'hooks/useHelpCenterSessionTracking'
import { FeedbackOptionsModel } from 'types/models/help-center-feedback'

import DenyHelpCenterFeedbackModal from '../DenyHelpCenterFeedbackModal'

const DenyHelpCenterFeedback = ({
  primary,
  title,
  inverse,
  theme,
  size,
}: Partial<ConversationActionProps>) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [problemOptions, setProblemOptions] = useState<Array<FeedbackOptionsModel>>([])

  const userId = useSession().user?.id
  const { conversation } = useConversationContext()
  const conversationId = conversation?.id

  const { fetch, isLoading: isFetchProblemOptionsLoading } = useFetch(getFeedbackProblemOptions)

  const { helpCenterSessionId } = useHelpCenterSessionTracking()

  const handleOpenModal = async () => {
    if (!conversationId || !userId) return

    const response = await fetch(userId, conversationId.toString())
    if (response.data?.problem_options) {
      setProblemOptions(transformFeedbackProblemOptions(response.data.problem_options))
      setIsModalOpen(true)
    }
  }

  function handleCloseModal() {
    setIsModalOpen(false)
  }

  async function handleSubmit(selectedOption: number | undefined, message: string) {
    if (!conversationId || !userId || !selectedOption) return

    setIsSubmitting(true)
    await sendFeedbackProblemFeedback({
      userId,
      conversationId: Number(conversationId),
      message,
      problemOptionId: selectedOption,
      helpCenterSessionId,
    })
    setIsSubmitting(false)
    setIsModalOpen(false)
  }

  return (
    <>
      <ConversationActionButton
        text={title}
        primary={primary}
        size={size}
        theme={theme}
        inverse={inverse}
        isLoading={isFetchProblemOptionsLoading || isSubmitting}
        onClick={handleOpenModal}
      />
      <DenyHelpCenterFeedbackModal
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        options={problemOptions}
        isOpen={isModalOpen}
      />
    </>
  )
}

export default DenyHelpCenterFeedback
